import React from "react";
import { Button } from "@/components/ui/button";
import { Brain, CheckCircle, ArrowRight } from "lucide-react";

const HeadWorksSpotlight = () => {
  const symptoms = [
    "Headaches",
    "Face pain",
    "Neck pain",
    "Jaw pain",
    "TMJ clicking",
    "Locking jaw",
    "Earaches",
    "Dizziness",
  ];

  return (
    <section
      className="py-20 md:py-28 bg-gradient-to-br from-[#C2410C]/5 via-[#C2410C]/10 to-[#C2410C]/5 relative overflow-hidden"
      data-testid="headworks-section"
    >
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#C2410C]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#D1FAE5]/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="relative order-2 lg:order-1">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&q=80"
                alt="TMD and jaw pain treatment at HeadWorks"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
              {/* Badge */}
              <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full">
                <p className="text-sm font-semibold text-[#C2410C]">Specialized TMD Care</p>
              </div>
            </div>

            {/* Stats Card */}
            <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-xl max-w-xs">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#C2410C]/10 rounded-full flex items-center justify-center">
                  <Brain className="w-6 h-6 text-[#C2410C]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">1 of 8</p>
                  <p className="text-sm text-gray-500">CCTT Certified in Canada</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-8 order-1 lg:order-2">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#C2410C]/10 text-[#C2410C] px-4 py-2 rounded-full text-sm font-semibold">
              <Brain className="w-4 h-4" />
              <span>HeadWorks Physiotherapy</span>
            </div>

            {/* Heading */}
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
              Specialized Treatment for{" "}
              <span className="text-[#C2410C]">Jaw & Face Pain</span>
            </h2>

            {/* Description */}
            <p className="text-lg text-gray-600 leading-relaxed">
              HeadWorks is our specialized TMD clinic led by Sharon Farquharson, 
              one of only eight Certified Cervical and Temporomandibular Therapists (CCTT) 
              in Canada. We focus on treating Temporomandibular Disorders (TMD), 
              orofacial and craniofacial pain.
            </p>

            {/* Symptoms Grid */}
            <div>
              <p className="font-semibold text-gray-900 mb-4">
                Do you experience any of these symptoms?
              </p>
              <div className="grid grid-cols-2 gap-3">
                {symptoms.map((symptom, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 text-gray-700"
                  >
                    <CheckCircle className="w-5 h-5 text-[#C2410C]" />
                    <span>{symptom}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <a
                href="https://bc-ice.janeapp.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="headworks-book-btn"
              >
                <Button className="w-full sm:w-auto bg-[#C2410C] hover:bg-[#9A3412] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
                  Book TMD Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a
                href="http://www.headworksphysio.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-[#C2410C] text-[#C2410C] hover:bg-[#C2410C] hover:text-white rounded-full px-8 py-6 text-lg font-semibold transition-all"
                >
                  Learn More About HeadWorks
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeadWorksSpotlight;
